import React, { useEffect, useState } from 'react';
import ProjectCard from '../components/ProjectCard';
import { Project } from '../types/Project';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDateWord } from '../utils/dateUtils';

const Projects: React.FC = () => {
  const { language, t } = useLanguage();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sortOrder, setSortOrder] = useState<string>('default');

  useEffect(() => {
    // Get sort order from URL if present
    const queryParams = new URLSearchParams(window.location.search);
    const order = queryParams.get('order');
    if (order) {
      setSortOrder(order);
    }

    // Fetch projects from the JSON file
    fetch('/projects.json')
      .then(response => response.json())
      .then(data => {
        // Get all projects from the new structure
        let projectsList = data.projects;
        
        // Process projects to include translated content
        const localizedProjects = projectsList.map((project: Project) => {
          // Get translation in current language or fall back to English
          const translation = project.translations[language] || project.translations.en;
          
          // Create a modified project object with the translated fields
          return {
            ...project,
            title: translation.title,
            description: translation.description,
            // Generate date-word automatically from the date field
            'date-word': formatDateWord(project.date, language)
          };
        });
        
        // Sort projects based on the sort order
        const sortedProjects = sortProjects(localizedProjects, sortOrder || 'default');
        
        setProjects(sortedProjects);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching projects:', error);
        setLoading(false);
      });
  }, [sortOrder, language]); // Make sure both sortOrder and language are dependencies

  // Improved sortProjects function with more robust sorting
  const sortProjects = (data: Project[], order: string): Project[] => {
    // Create a copy of the array to avoid mutating the original
    const sortedData = [...data];
    
    switch(order) {
      case 'asc':
        return sortedData.sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, {sensitivity: 'base'}));
      case 'desc':
        return sortedData.sort((a, b) => (b.title || '').localeCompare(a.title || '', undefined, {sensitivity: 'base'}));
      case 'new':
        return sortedData.sort((a, b) => {
          // Handle cases where date might be missing or invalid
          if (!a.date) return 1;
          if (!b.date) return -1;
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
      case 'old':
        return sortedData.sort((a, b) => {
          // Handle cases where date might be missing or invalid
          if (!a.date) return 1;
          if (!b.date) return -1;
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        });
      default:
        return sortedData;
    }
  };

  const handleSortChange = (order: string) => {
    // Update URL with the new sort order
    const url = new URL(window.location.href);
    url.searchParams.set('order', order);
    window.history.pushState({}, '', url);
    
    setSortOrder(order);
  };

  return (
    <>
      <header>
        <div className="header-box">
          <h1>{t('titles', 'myProjects')}</h1>
          <details className="dropdown">
            <summary style={{ userSelect: 'none' }}>{t('common', 'sort')}</summary>
            <ul className="dropdown-menu" style={{ marginLeft: 'calc(-24px)' }}>
              <li onClick={() => handleSortChange('asc')}>
                <a className="dropdown-item" href="#">{t('titles', 'AtoZ')}</a>
              </li>
              <li onClick={() => handleSortChange('desc')}>
                <a className="dropdown-item" href="#">{t('titles', 'ZtoA')}</a>
              </li>
              <hr />
              <li onClick={() => handleSortChange('new')}>
                <a className="dropdown-item" href="#">{t('titles', 'newestFirst')}</a>
              </li>
              <li onClick={() => handleSortChange('old')}>
                <a className="dropdown-item" href="#">{t('titles', 'oldestFirst')}</a>
              </li>
            </ul>
          </details>
        </div>
        <h4>{t('titles', 'someProjectsInOtherLang')}</h4>
      </header>
      <main id="projects">
        {loading ? (
          <p style={{ color: 'var(--muted-color)', fontSize: '.9rem' }}>Loading {t('common', 'projects').toLowerCase()}...</p>
        ) : (
          projects.map(project => (
            <ProjectCard key={project.id} project={project} />
          ))
        )}
      </main>
    </>
  );
};

export default Projects;